import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Plus, Trash2, Gamepad2, Plane } from 'lucide-react'

// Define types here since we don't have access to the types file
interface Game {
  id: string
  title: string
  type: 'best_of_three' | 'total_score'
  cost_per_game: number
  max_players: number
  current_players: number
  expires_at: string
  status: 'open' | 'closed' | 'in_progress'
  created_at: string
}

interface Pool {
  id: string
  title: string
  max_amount: number
  current_players: number
  required_players: number
  entry_fee: number
  game_type_id: string
  status: 'open' | 'in_progress' | 'completed'
  flight_details?: any
  created_at: string
}

import { supabase } from '@/lib/supabase'

export default function AdminGamesPage() {
  const [games, setGames] = useState<Game[]>([])
  const [pools, setPools] = useState<Pool[]>([])
  const [editingGame, setEditingGame] = useState<Game | null>(null)
  const [editingPool, setEditingPool] = useState<Pool | null>(null)
  const [deletingGame, setDeletingGame] = useState<Game | null>(null)
  const [deletingPool, setDeletingPool] = useState<Pool | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([loadGames(), loadPools()])
      .finally(() => setLoading(false))
  }, [])

  async function loadGames() {
    try {
      const { data, error } = await supabase
        .from('games')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setGames(data || [])
    } catch (error) {
      console.error('Failed to load games:', error)
    }
  }

  async function loadPools() {
    try {
      const { data, error } = await supabase
        .from('game_pools')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setPools(data || [])
    } catch (error) {
      console.error('Failed to load pools:', error)
    }
  }

  const handleDelete = async (game: Game) => {
    try {
      const { error } = await supabase
        .from('games')
        .delete()
        .eq('id', game.id)

      if (error) throw error
      
      await loadGames()
      setDeletingGame(null)
    } catch (error) {
      console.error('Failed to delete game:', error)
      alert('Failed to delete game')
    }
  }

  const handleDeletePool = async (pool: Pool) => {
    try {
      const { error } = await supabase
        .from('game_pools')
        .delete()
        .eq('id', pool.id)

      if (error) throw error
      
      await loadPools()
      setDeletingPool(null)
    } catch (error) {
      console.error('Failed to delete pool:', error)
      alert('Failed to delete pool')
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingGame) return

    const form = e.target as HTMLFormElement
    const formData = new FormData(form)

    try {
      const { error } = await supabase
        .from('games')
        .update({
          title: formData.get('title'),
          cost_per_game: parseInt(formData.get('cost') as string),
          expires_at: formData.get('expires_at'),
          max_players: parseInt(formData.get('max_players') as string)
        })
        .eq('id', editingGame.id)

      if (error) throw error
      
      await loadGames()
      setEditingGame(null)
    } catch (error) {
      console.error('Failed to update game:', error)
      alert('Failed to update game')
    }
  }

  const handleSavePool = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingPool) return

    const form = e.target as HTMLFormElement
    const formData = new FormData(form)

    try {
      const { error } = await supabase
        .from('game_pools')
        .update({
          title: formData.get('title'),
          max_amount: parseFloat(formData.get('maxAmount') as string),
          required_players: parseInt(formData.get('requiredPlayers') as string),
          entry_fee: parseInt(formData.get('entryFee') as string),
          game_type_id: formData.get('gameTypeId')
        })
        .eq('id', editingPool.id)

      if (error) throw error
      
      await loadPools()
      setEditingPool(null)
    } catch (error) {
      console.error('Failed to update pool:', error)
      alert('Failed to update pool')
    }
  }

  if (loading) {
    return <div>Loading games...</div>
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold">Games & Pools Management</h1>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Game
        </Button>
      </div>
      
      <Tabs defaultValue="games" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="games" className="flex items-center gap-2">
            <Gamepad2 className="w-4 h-4" />
            Regular Games
          </TabsTrigger>
          <TabsTrigger value="pools" className="flex items-center gap-2">
            <Plane className="w-4 h-4" />
            Flight Pools
          </TabsTrigger>
        </TabsList>

        <TabsContent value="games" className="space-y-6">
        {games.map((game) => (
          <Card key={game.id}>
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                <span>{game.type === 'best_of_three' ? 'Best of 3' : 'Total Score'}</span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setEditingGame(game)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => setDeletingGame(game)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Title:</span>
                  <span>{game.title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cost:</span>
                  <span>{game.cost_per_game} Coins</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Players:</span>
                  <span>{game.current_players}/{game.max_players}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expires:</span>
                  <span>{new Date(game.expires_at).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="capitalize">{game.status}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        </TabsContent>

        <TabsContent value="pools" className="space-y-6">
          {pools.map((pool) => (
            <Card key={pool.id}>
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  <span>{pool.title}</span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setEditingPool(pool)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => setDeletingPool(pool)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Max Prize:</span>
                    <span>${pool.max_amount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Players:</span>
                    <span>{pool.current_players}/{pool.required_players}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Entry Fee:</span>
                    <span>{pool.entry_fee} Coins</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <span className="capitalize">{pool.status}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>


      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingGame} onOpenChange={() => setDeletingGame(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the game
              and remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingGame(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deletingGame && handleDelete(deletingGame)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <Dialog open={isCreating} onOpenChange={setIsCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Game</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault()
            const form = e.target as HTMLFormElement
            const formData = new FormData(form)

            try {
              const { error } = await supabase
                .from('games')
                .insert({
                  title: formData.get('title'),
                  type: formData.get('type'),
                  cost_per_game: parseInt(formData.get('cost') as string),
                  expires_at: formData.get('expires_at'),
                  max_players: parseInt(formData.get('max_players') as string),
                  current_players: 0,
                  status: 'open'
                })

              if (error) throw error
              
              await loadGames()
              setIsCreating(false)
            } catch (error) {
              console.error('Failed to create game:', error)
              alert('Failed to create game')
            }
          }}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  name="title"
                  placeholder="Enter game title"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Game Type</Label>
                <select
                  id="type"
                  name="type"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  required
                >
                  <option value="best_of_three">Best of Three</option>
                  <option value="total_score">Total Score</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cost">Cost (Coins)</Label>
                <Input
                  id="cost"
                  name="cost"
                  type="number"
                  min="0"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_players">Max Players</Label>
                <Input
                  id="max_players"
                  name="max_players"
                  type="number"
                  min="1"
                  defaultValue="100"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expires_at">Expires At</Label>
                <Input
                  id="expires_at"
                  name="expires_at"
                  type="datetime-local"
                  required
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsCreating(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Game</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pool Delete Confirmation */}
      <AlertDialog open={!!deletingPool} onOpenChange={() => setDeletingPool(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the pool
              and remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingPool(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deletingPool && handleDeletePool(deletingPool)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={!!editingGame} onOpenChange={() => setEditingGame(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Game</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  name="title"
                  defaultValue={editingGame?.title || ''}
                  placeholder="Enter game title"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cost">Cost (Coins)</Label>
                <Input
                  id="cost"
                  name="cost"
                  type="number"
                  defaultValue={editingGame?.cost_per_game || 0}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_players">Max Players</Label>
                <Input
                  id="max_players"
                  name="max_players"
                  type="number"
                  defaultValue={editingGame?.max_players || 100}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expires_at">Expires At</Label>
                <Input
                  id="expires_at"
                  name="expires_at"
                  type="datetime-local"
                  defaultValue={editingGame?.expires_at ? editingGame.expires_at.split('.')[0] : ''}
                  required
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setEditingGame(null)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pool Edit Dialog */}
      <Dialog open={!!editingPool} onOpenChange={() => setEditingPool(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Pool</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSavePool}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  name="title"
                  defaultValue={editingPool?.title || ''}
                  placeholder="Enter pool title"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gameTypeId">Game Type</Label>
                <select
                  id="gameTypeId"
                  name="gameTypeId"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1"
                  defaultValue={editingPool?.game_type_id}
                  required
                >
                  <option value="trivia">Travel Trivia</option>
                  <option value="memory">Memory Match</option>
                  <option value="word_puzzle">Word Puzzle</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxAmount">Max Prize Amount ($)</Label>
                <Input
                  id="maxAmount"
                  name="maxAmount"
                  type="number"
                  min="100"
                  max="5000"
                  defaultValue={editingPool?.max_amount}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="requiredPlayers">Required Players</Label>
                <Input
                  id="requiredPlayers"
                  name="requiredPlayers"
                  type="number"
                  min="2"
                  max="100"
                  defaultValue={editingPool?.required_players}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="entryFee">Entry Fee (Coins)</Label>
                <Input
                  id="entryFee"
                  name="entryFee"
                  type="number"
                  min="1"
                  max="100"
                  defaultValue={editingPool?.entry_fee}
                  required
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setEditingPool(null)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}